import { TEST_VENUES } from './testVenues.js';

/**
 * Fictional people used by the mock directory (Entra) and the mock D365 seed.
 * 25 directory users: 5 per seeded venue (V001–V003), 2 regional/corporate,
 * 6 unassigned (available as hires for a new venue), 2 disabled.
 */
export interface MockPerson {
  upn: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  /** Seeded placement in D365, if any: `${companyId}:${slot}` or `corp:${slot}`. */
  placement: string | null;
}

/**
 * Domain of the venue staff in the people sheets (firstname.lastname@…). The demo tenant's
 * domain, so users the live slice creates in D365 sign in like real accounts there.
 * App sign-in personas (auth.ts) keep @legends.example.
 */
export const PEOPLE_DOMAIN = 'rsmd365s.com';

const person = (first: string, last: string, placement: string | null, enabled = true): MockPerson => ({
  upn: `${first}.${last}@${PEOPLE_DOMAIN}`.toLowerCase(),
  firstName: first,
  lastName: last,
  enabled,
  placement,
});

export const VENUE_SLOTS = ['GM', 'FM', 'BUY', 'AP', 'PM'] as const;
export type VenueSlot = (typeof VENUE_SLOTS)[number];

export const MOCK_PEOPLE: readonly MockPerson[] = [
  person('Avery', 'Holt', 'V001:GM'),
  person('Blake', 'Moreno', 'V001:FM'),
  person('Cam', 'Nguyen', 'V001:BUY'),
  person('Dana', 'Kowalski', 'V001:AP'),
  person('Eli', 'Brennan', 'V001:PM'),
  person('Frankie', 'Osei', 'V002:GM'),
  person('Gray', 'Lindqvist', 'V002:FM'),
  person('Harper', 'Iyer', 'V002:BUY'),
  person('Indy', 'Castillo', 'V002:AP'),
  person('Jules', 'Okafor', 'V002:PM'),
  person('Kai', 'Thornton', 'V003:GM'),
  person('Lane', 'Mbeki', 'V003:FM'),
  person('Marlow', 'Sato', 'V003:BUY'),
  person('Noel', 'Fitzgerald', 'V003:AP'),
  person('Oakley', 'Reyes', 'V003:PM'),
  person('Parker', 'Vance', 'corp:REG-FIN-01'),
  person('Quinn', 'Ashford', 'corp:CORP-CFO'),
  person('Reese', 'Dalton', null),
  person('Sage', 'Whitaker', null),
  person('Tatum', 'Ellison', null),
  person('Umi', 'Hargrove', null),
  person('Val', 'Petrosyan', null),
  person('Wren', 'Calloway', null),
  person('Xen', 'Albright', null, false),
  person('Yael', 'Donovan', null, false),
  // Staff of the RSM1–RSM8 test venues (testVenues.ts), so the Entra check finds them.
  ...TEST_VENUES.flatMap((v) => v.staff.map(([first, last]) => person(first, last, null))),
];
